import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { Modal } from '@diamond-design/components';
import CtaButton from '../../ctaButton/ctaButton';
import useSetHeroImg from '../../../hooks/useSetHeroImg';
import styles from './heroHomeTemplate.module.scss';
import HeroHomePrequalCard from '../../heroHomePrequal/prequalCard/heroHomePrequalCard';
import HeroHomePrequal from '../../heroHomePrequal/prequaldefault/heroHomePrequal';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import SmartImage from '../../smartImage/smartImage';
import Graphicon from './images/Graphicon.png';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';

function HeroHomeTemplateFullBackground({
  title = '',
  titleBlue = '',
  heroDesc = '',
  image = null,
  mobImage,
  prequalItems = null,
  backgroundSize = 'cover',
  isExistingUser = false,
  isPersonalization = false,
}) {
  const [docked, setDocked] = useState(false);
  const { width } = useWindowDimensions();
  const [isMobile, setIsMobile] = useState(false);
  const [showOffer, setShowOffer] = useState(null);
  const [loading, setLoading] = useState(isExistingUser && isPersonalization);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);

  useEffect(() => {
    if (width < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [width]);
  useEffect(() => {
    const isDocked =
      window.document.documentElement.scrollTop >= 0 || window.scrollY >= 0;
    setDocked(isDocked);
  }, []);

  useEffect(() => {
    const getMemberOffer = async () => {
      try {
        const response = await fetch('/api/get-member-offer-service', {
          method: 'POST',
        });

        const data = await response.json();

        if (!response.ok || data?.status === 'ERROR') {
          throw new Error(data?.message || `API error ${response.status}`);
        }

        if (data.offers.length > 0) {
          const {
            eventName,
            card,
            variant,
            campaign_id: campaignId,
          } = data.offers[0].HeapTagging;
          await trackHeapEvent(
            {
              card,
              variant,
              campaignId,
              placement: 'Homepage',
              url: data.offers[0]?.URL,
            },
            `${eventName}_view`
          );
          setShowOffer(data.offers);
          setLoading(false);
        } else {
          // eslint-disable-next-line no-console
          console.error('Get Member Offer check returned no offers:', data);
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        // eslint-disable-next-line no-console
        console.error('Get Member Offer check failed:', err);
      }
    };

    if (isExistingUser && isPersonalization) {
      getMemberOffer();
    } else setLoading(false);
  }, [isExistingUser, isPersonalization]);

  const isOfferAvailable = useMemo(() => {
    return Array.isArray(showOffer) && showOffer.length > 0;
  }, [showOffer]);

  const heroImg = useSetHeroImg(image, mobImage, width);

  const displayPrequal = useCallback(() => {
    if (prequalItems?.prequalType === 'Home Card') {
      return (
        <HeroHomePrequalCard
          {...prequalItems}
          isPersonalization={isOfferAvailable}
        />
      );
    }
    return (
      <div
        className={styles.heroSingleImagePrequalDefault}
        data-testid="prequal"
      >
        <HeroHomePrequal {...prequalItems} />
      </div>
    );
  }, [prequalItems, isOfferAvailable]);

  const handleOffferCTAClick = async (offer) => {
    try {
      const {
        eventName,
        card,
        variant,
        campaign_id: campaignId,
      } = offer.HeapTagging;
      await trackHeapEvent(
        {
          card,
          variant,
          campaignId,
          placement: 'Homepage',
          url: offer?.URL,
        },
        `${eventName}_click`
      );
    } catch (err) {
      Promise.reject(err); // it's unclear what the app expects on a failure
    }
  };

  const renderOfferView = useCallback(
    () => (
      <div className={styles.content}>
        {showOffer?.map((offer) => (
          <div
            className={styles.heroHomeOfferWrapper}
            data-testid="personalization"
            key={offer.pgi}
          >
            <div className={styles.halfView}>
              <h1 className={styles.offerMessage}>Welcome back</h1>
            </div>
            <div className={`${styles.halfView} ${styles.offerContainer}`}>
              <div className={styles.offerWrapper}>
                <div className={styles.offerHeaderContent}>
                  <div className={styles.iconWrapper}>
                    <SmartImage src={Graphicon} alt="Feature Graphicon" />
                  </div>
                  <div className={styles.headerContent}>
                    <div className={styles.offerNote}>{offer.Title}</div>
                    <div className={styles.offerHeadline}>{offer.Desc}</div>
                  </div>
                </div>
                <div className={styles.offerDescription}>{offer.Detail}</div>
                <div className={styles.offerFooter}>
                  <div
                    className={styles.offerDisclaimer}
                    onClick={() => setShowDisclosureModal(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowDisclosureModal(true);
                      }
                    }}
                  >
                    {offer.DisclosureLabel}
                  </div>
                  <CtaButton
                    className={styles.offerCtaButton}
                    label={offer.EditButtonLabel}
                    variant="outline"
                    defaultColor
                    fullWidth={isMobile}
                    href={offer.URL}
                    onClick={() => handleOffferCTAClick(offer)}
                  />
                </div>
              </div>
            </div>
            {showDisclosureModal && (
              <Modal
                open={showDisclosureModal}
                size="xs"
                onDismiss={() => setShowDisclosureModal(false)}
                title={offer.DisclosureLabel}
              >
                <div className={styles.disclosureContent}>
                  {parse(offer.Disclosure)}
                </div>
                <CtaButton
                  className={styles.disclosureButton}
                  label="Ok"
                  variant="solid"
                  defaultColor
                  fullWidth
                  onClick={() => setShowDisclosureModal(false)}
                />
              </Modal>
            )}
          </div>
        ))}
      </div>
    ),
    [showOffer, showDisclosureModal, isMobile]
  );

  const renderNoOfferView = useCallback(() => {
    return (
      <>
        <div className={styles.contentContainer}>
          <div className={styles.content}>
            <div className={styles.heroHeader}>
              {title || titleBlue ? (
                <h1
                  className={`${styles.headerTitleScroll} ${
                    docked && styles.headerTitleScrollDocked
                  }`}
                >
                  {title} {titleBlue && <strong>{titleBlue}</strong>}
                </h1>
              ) : null}
              {heroDesc && (
                <div
                  className={`${styles.headerDescScroll} ${
                    docked && styles.headerDescScrollDocked
                  }`}
                >
                  {parse(heroDesc)}
                </div>
              )}
            </div>
            {!isMobile && prequalItems && displayPrequal()}
          </div>
        </div>
        {heroImg?.src && (
          <div
            className={`${
              prequalItems?.prequalType === 'Home Card'
                ? styles.heroClipImageCardHeight
                : styles.heroClipImageHeight
            } ${styles.heroClipImage} ${
              backgroundSize === 'contain' && styles.bgContain
            }`}
          >
            <SmartImage
              src={heroImg}
              alt={heroImg?.alt}
              width={1728}
              height={700}
              priority
            />
          </div>
        )}
      </>
    );
  }, [
    title,
    titleBlue,
    heroDesc,
    docked,
    prequalItems,
    displayPrequal,
    heroImg,
    backgroundSize,
    isMobile,
  ]);

  if (loading) {
    return <div className={styles.loading} data-testid="loading" />;
  }

  return (
    <section className={styles.heroPrequalWithSingleImage}>
      <div
        className={`${styles.sectionContainer} ${
          isOfferAvailable ? styles.offerSection : ''
        }`}
      >
        {isOfferAvailable ? renderOfferView() : renderNoOfferView()}
      </div>
      {isMobile &&
        !isOfferAvailable &&
        prequalItems?.prequalType === 'Home Card' && (
          <div className={styles.heroHometemplatePrequalSectionForCardMobile}>
            <div className={styles.heroHomePrequalMobile}>
              <HeroHomePrequalCard {...prequalItems} />
            </div>
          </div>
        )}
      {isOfferAvailable && (
        <div className={styles.prequalWrapper}>
          <div
            className={`${styles.content} ${
              isMobile ? styles.heroHomePrequalMobile : ''
            }`}
          >
            {prequalItems && displayPrequal()}
          </div>
        </div>
      )}
    </section>
  );
}

HeroHomeTemplateFullBackground.propTypes = {
  title: PropTypes.string,
  heroDesc: PropTypes.string,
  titleBlue: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }),
  mobImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }),
  prequalItems: PropTypes.object,
  backgroundSize: PropTypes.string,
};

export default HeroHomeTemplateFullBackground;
